import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowRight, Check, X, Mail, Lock, User, Eye, EyeOff, Sparkles } from 'lucide-react';
import SEO, { PageSEO } from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

import { API } from '../config/api';

// TEMPORARY: Hide Google login while OAuth app is under verification
const GOOGLE_LOGIN_ENABLED = true;  // Now enabled via Supabase!
const DISCORD_LOGIN_ENABLED = true; // Discord login via Supabase
const GITHUB_LOGIN_ENABLED = true;  // GitHub login via Supabase

// Password strength validation
const validatePassword = (password) => {
  const requirements = {
    minLength: password.length >= 12,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
  
  const isValid = Object.values(requirements).every(Boolean);
  const strength = Object.values(requirements).filter(Boolean).length;
  
  return { requirements, isValid, strength };
};

// Password requirements component (moved outside Login to avoid re-renders)
const PasswordRequirements = ({ password, show }) => {
  if (!show || !password) return null;
  const validation = validatePassword(password);
  
  return (
    <div className="mt-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
      <p className="text-xs text-slate-400 mb-2">Password requirements:</p>
      <div className="grid grid-cols-2 gap-1.5">
        {[
          { key: 'minLength', label: '12+ characters' },
          { key: 'hasUppercase', label: 'Uppercase letter' },
          { key: 'hasLowercase', label: 'Lowercase letter' },
          { key: 'hasNumber', label: 'Number' },
          { key: 'hasSpecial', label: 'Special character' },
        ].map(({ key, label }) => (
          <div key={key} className="flex items-center gap-1.5 text-xs">
            {validation.requirements[key] ? (
              <Check className="h-3 w-3 text-green-400" />
            ) : (
              <X className="h-3 w-3 text-slate-500" />
            )}
            <span className={validation.requirements[key] ? 'text-green-400' : 'text-slate-500'}>
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // reCAPTCHA removed for performance - backend accepts without it
  const executeRecaptcha = null;
  const { refreshUser, isAuthenticated, login, signUp, signInWithGoogle, isSupabaseEnabled } = useAuth();
  
  // Set default tab based on URL path
  const [activeTab, setActiveTab] = useState(
    location.pathname === '/register' ? 'register' : 'login'
  );
  
  // Update tab when URL changes
  useEffect(() => {
    if (location.pathname === '/register') {
      setActiveTab('register');
    } else if (location.pathname === '/login') {
      setActiveTab('login');
    }
  }, [location.pathname]);
  
  const [loginData, setLoginData] = useState({ email: '', password: '', rememberMe: false });
  const [registerData, setRegisterData] = useState({ email: '', password: '', first_name: '', last_name: '', role: 'user', rememberMe: false });
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetData, setResetData] = useState({ email: '', resetCode: '', newPassword: '' });
  const [showReset, setShowReset] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  
  // Email verification state
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Check for redirect param first, otherwise go to dashboard
      const redirectTo = searchParams.get('redirect');
      if (redirectTo && redirectTo.startsWith('/')) {
        navigate(redirectTo);
      } else {
        navigate('/blackboard');
      }
    }
  }, [isAuthenticated, navigate, searchParams]);
  
  // Warmup backend on page load
  useEffect(() => {
    const warmupBackend = async () => {
      try {
        await Promise.all([
          axios.get(`${API}/ping`, { timeout: 10000 }).catch(() => {}),
          axios.get(`${API}/health`, { timeout: 10000 }).catch(() => {})
        ]);
        console.log('[Auth] Backend warmed up successfully');
      } catch (err) {
        // Silent fail
      }
    };
    warmupBackend();
  }, []);
  
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      toast.success('🎉 You\'ve been referred! Sign up to get 5 bonus credits!');
    }
    
    // Pre-fill email and name from URL params (for guest registration flow)
    const prefillEmail = searchParams.get('email');
    const prefillName = searchParams.get('name');
    if (prefillEmail) {
      setRegisterData(prev => ({ ...prev, email: prefillEmail }));
      setActiveTab('register');
      toast.info('Create an account to track your event registrations!');
    }
    if (prefillName) {
      const names = prefillName.trim().split(' ');
      const firstName = names[0] || '';
      const lastName = names.slice(1).join(' ') || '';
      setRegisterData(prev => ({ ...prev, first_name: firstName, last_name: lastName }));
    }
    
    const token = searchParams.get('token');
    const discordConnected = searchParams.get('discord');
    const googleConnected = searchParams.get('google');
    const isNewUser = searchParams.get('new');
    
    if (token) {
      localStorage.setItem('auth_token', token);
      refreshUser();
      
      if (discordConnected === 'true') {
        toast.success('🎮 Discord account connected!');
      } else if (googleConnected === 'true') {
        toast.success('✨ Google account connected!');
      }
      
      if (isNewUser === 'true') {
        toast.success('🎉 Welcome to Squidemy! Your account has been created.');
        navigate('/blackboard');
      } else {
        // Respect redirect param after OAuth callback
        const redirectTo = searchParams.get('redirect');
        if (redirectTo && redirectTo.startsWith('/')) {
          navigate(redirectTo);
        } else {
          navigate('/blackboard');
        }
      }
      return;
    }
    
    const error = searchParams.get('error');
    if (error) {
      const errorMessages = {
        'no_code': 'Authentication was cancelled or failed',
        'token_exchange_failed': 'Failed to complete authentication',
        'user_info_failed': 'Failed to get user information',
        'user_creation_failed': 'Failed to create your account',
        'email_exists': 'An account with this email already exists. Please sign in with email/password.',
        'oauth_error': 'Authentication provider returned an error',
      };
      toast.error(errorMessages[error] || `Authentication failed: ${error}`);
    }
  }, [searchParams, navigate, refreshUser]);
  
  // Google OAuth handler via Supabase
  const handleGoogleAuth = async () => {
    if (!isSupabaseEnabled) {
      toast.error('Google login is temporarily unavailable');
      return;
    }
    
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
      // Supabase will redirect to the OAuth provider
    } catch (err) {
      console.error('Google auth error:', err);
      toast.error('Failed to connect to Google');
      setIsGoogleLoading(false);
    }
  };

  // Discord OAuth handler via Supabase
  const handleDiscordAuth = async () => {
    if (!isSupabaseEnabled) {
      toast.error('Discord login is temporarily unavailable');
      return;
    }
    
    setIsGoogleLoading(true); // Reuse loading state
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      if (error) throw error;
    } catch (err) {
      console.error('Discord auth error:', err);
      toast.error('Failed to connect to Discord');
      setIsGoogleLoading(false);
    }
  };

  // GitHub OAuth handler via Supabase
  const handleGitHubAuth = async () => {
    if (!isSupabaseEnabled) {
      toast.error('GitHub login is temporarily unavailable');
      return;
    }
    
    setIsGoogleLoading(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      if (error) throw error;
    } catch (err) {
      console.error('GitHub auth error:', err);
      toast.error('Failed to connect to GitHub');
      setIsGoogleLoading(false);
    }
  };

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      let recaptchaToken = null;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('login');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA failed, proceeding without:', recaptchaError);
        }
      }
      
      // Use AuthContext's login method (Supabase only)
      const result = await login(loginData.email, loginData.password);
      
      // Check if user needs password reset (legacy user migration)
      if (result?.needsPasswordReset) {
        toast.info('Please check your email to reset your password and continue.', { duration: 8000 });
        // Show the password reset form
        setShowReset(true);
        setForgotEmail(loginData.email);
        return;
      }
      
      await refreshUser();
      toast.success('Welcome back!');
      // Respect redirect param after login
      const redirectTo = searchParams.get('redirect');
      if (redirectTo && redirectTo.startsWith('/')) {
        navigate(redirectTo);
      } else {
        navigate('/blackboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      
      if (err.response?.status === 403 && err.response?.data?.detail?.includes('verify your email')) {
        setUnverifiedEmail(loginData.email);
        setShowEmailVerification(true);
        toast.error('Please verify your email before logging in');
      } else if (err.message?.includes('Invalid email or password')) {
        toast.error('Invalid email or password. If you haven\'t logged in recently, please use "Forgot Password" below.');
      } else if (err.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else if (err.message) {
        toast.error(err.message);
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Register handler
  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (!validatePassword(registerData.password).isValid) {
      toast.error('Please meet all password requirements');
      setIsLoading(false);
      return;
    }
    
    try {
      // Use AuthContext's signUp which handles both Supabase and legacy
      const result = await signUp(registerData.email, registerData.password, registerData.name, referralCode);
      
      if (result.needsConfirmation) {
        // Supabase requires email confirmation
        setUnverifiedEmail(registerData.email);
        setShowEmailVerification(true);
        toast.success('Account created! Please check your email to verify.');
      } else if (result.token) {
        // Legacy registration returned a token
        localStorage.setItem('auth_token', result.token);
        await refreshUser();
        toast.success('Welcome to Squidemy!');
        navigate('/blackboard');
      } else if (result.success) {
        // Supabase registration succeeded without confirmation required
        await refreshUser();
        toast.success('Welcome to Squidemy!');
        navigate('/blackboard');
      }
    } catch (err) {
      console.error('Registration error:', err);
      if (err.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else if (err.message) {
        toast.error(err.message);
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot password handler - Uses Supabase password reset
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Use Supabase password reset
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`
      });
      
      if (error) throw error;
      
      toast.success('Password reset link sent to your email! Please check your inbox.', { duration: 6000 });
      setShowReset(false);
    } catch (err) {
      // If Supabase fails (user might not exist in Supabase yet), try legacy
      try {
        const resp = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
        if (resp.data?.is_oauth) {
          const provider = resp.data.provider || 'your social account';
          toast.info(`This account uses ${provider}. Log in with ${provider} first, then set a password from your Profile page.`, { duration: 8000 });
          setShowReset(false);
        } else {
          setResetData({ ...resetData, email: forgotEmail });
          setResetCodeSent(true);
          toast.success('Reset code sent to your email!');
        }
      } catch (legacyErr) {
        toast.error(err.message || legacyErr.response?.data?.detail || 'Failed to send reset code');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password handler
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (!validatePassword(resetData.newPassword).isValid) {
      toast.error('Please meet all password requirements');
      setIsLoading(false);
      return;
    }
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetData.email,
        reset_code: resetData.resetCode,
        new_password: resetData.newPassword
      });
      toast.success('Password reset successful! Please sign in.');
      setShowReset(false);
      setResetCodeSent(false);
      setResetData({ email: '', resetCode: '', newPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password reset failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend verification email
  const handleResendVerification = async () => {
    setIsResendingEmail(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: unverifiedEmail });
      toast.success('Verification email sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setIsResendingEmail(false);
    }
  };

  const passwordValidation = validatePassword(activeTab === 'register' ? registerData.password : resetData.newPassword);

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <SEO {...PageSEO.login} />
      
      {/* Left Side - Branding & Visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-purple-900 via-violet-800 to-fuchsia-900">
        {/* Animated blob shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-fuchsia-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-violet-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          <div className="absolute bottom-1/3 left-10 w-48 h-48 bg-pink-500/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }} />
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <Link to="/" className="mb-8">
            <picture>
              <source srcSet="/squiddy_logo.webp" type="image/webp" />
              <img 
                src="/squiddy_logo.png" 
                alt="Squidemy" 
                className="w-32 h-32 object-contain drop-shadow-2xl hover:scale-105 transition-transform"
                width="128"
                height="128"
                loading="eager"
              />
            </picture>
          </Link>
          
          <h1 className="text-4xl font-bold mb-4 text-center">
            Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-300 to-violet-300">Squidemy</span>
          </h1>
          
          <p className="text-lg text-purple-100/80 text-center max-w-md mb-8">
            Master IT & Cybersecurity with hands-on training, expert-led bootcamps, and real-world labs.
          </p>
          
          {/* Feature highlights */}
          <div className="space-y-4 text-sm">
            {[
              'Industry-recognized certifications',
              'Hands-on virtual labs',
              'Expert instructors',
              'Career-focused curriculum'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-purple-100/90">
                <div className="w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-violet-500 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
                {feature}
              </div>
            ))}
          </div>
        </div>
        
        {/* Bottom decoration */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 to-transparent" />
      </div>
      
      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-slate-950">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <Link to="/">
              <picture>
                <source srcSet="/squiddy_logo.webp" type="image/webp" />
                <img src="/squiddy_logo.png" alt="Squidemy" className="w-20 h-20 object-contain" width="80" height="80" loading="eager" />
              </picture>
            </Link>
          </div>
          
          {/* Email Verification State */}
          {showEmailVerification ? (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                <Mail className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Check Your Email</h2>
              <p className="text-slate-400 mb-6">
                We&apos;ve sent a verification link to<br />
                <span className="text-white font-medium">{unverifiedEmail}</span>
              </p>
              <Button
                onClick={handleResendVerification}
                disabled={isResendingEmail}
                className="w-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700"
              >
                {isResendingEmail ? 'Sending...' : 'Resend Verification Email'}
              </Button>
              <button
                onClick={() => setShowEmailVerification(false)}
                className="mt-4 text-sm text-slate-400 hover:text-white transition-colors"
              >
                ← Back to sign in
              </button>
            </div>
          ) : showReset ? (
            /* Password Reset Form */
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Reset Password</h2>
              <p className="text-slate-400 mb-6">
                {resetCodeSent ? 'Enter the code sent to your email' : 'Enter your email to receive a reset code'}
              </p>
              
              {!resetCodeSent ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <Label className="text-slate-300 text-sm">Email Address</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                      <Input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="pl-10 bg-slate-900 border-slate-700 text-white focus:border-purple-500"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 py-6"
                  >
                    {isLoading ? 'Sending...' : 'Send Reset Code'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <Label className="text-slate-300 text-sm">Reset Code</Label>
                    <Input
                      type="text"
                      value={resetData.resetCode}
                      onChange={(e) => setResetData({ ...resetData, resetCode: e.target.value.toUpperCase().trim() })}
                      className="mt-1 bg-slate-900 border-slate-700 text-white focus:border-purple-500 uppercase tracking-widest font-mono"
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">New Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={resetData.newPassword}
                        onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                        className="pl-10 pr-10 bg-slate-900 border-slate-700 text-white focus:border-purple-500"
                        placeholder="••••••••••••"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <PasswordRequirements password={resetData.newPassword} show={true} />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading || !validatePassword(resetData.newPassword).isValid}
                    className="w-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 py-6"
                  >
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              )}
              
              <button
                onClick={() => { setShowReset(false); setResetCodeSent(false); }}
                className="mt-4 text-sm text-slate-400 hover:text-white transition-colors"
              >
                ← Back to sign in
              </button>
            </div>
          ) : (
            /* Main Login/Register Form */
            <div>
              {/* Tab Switcher */}
              <div className="flex mb-8 bg-slate-900 rounded-xl p-1">
                <button
                  onClick={() => {
                    setActiveTab('login');
                    navigate('/login', { replace: true });
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                    activeTab === 'login'
                      ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-lg'
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="login-tab"
                >
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setActiveTab('register');
                    navigate('/register', { replace: true });
                  }}
                  className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                    activeTab === 'register'
                      ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-lg'
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="register-tab"
                >
                  Create Account
                </button>
              </div>
              
              {activeTab === 'login' ? (
                /* Login Form */
                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <Label className="text-slate-300 text-sm">Email Address</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                      <Input
                        type="email"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-purple-500 focus:ring-purple-500/20"
                        placeholder="you@example.com"
                        required
                        data-testid="login-email"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300 text-sm">Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="pl-10 pr-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-purple-500 focus:ring-purple-500/20"
                        placeholder="••••••••••••"
                        required
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={loginData.rememberMe}
                        onCheckedChange={(checked) => setLoginData({ ...loginData, rememberMe: checked })}
                        className="border-slate-600 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                      />
                      <span className="text-sm text-slate-400">Remember me</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowReset(true)}
                      className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 py-6 text-base font-medium shadow-lg shadow-purple-500/25 transition-all hover:shadow-purple-500/40"
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Signing in...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Sign In
                        <ArrowRight className="h-5 w-5" />
                      </span>
                    )}
                  </Button>
                  
                  {/* Social Login Buttons - Google and Discord via Supabase */}
                  {(GOOGLE_LOGIN_ENABLED || DISCORD_LOGIN_ENABLED) && (
                    <>
                      <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-slate-700" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-slate-950 px-2 text-slate-500">Or continue with</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        {GOOGLE_LOGIN_ENABLED && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleGoogleAuth}
                            disabled={isGoogleLoading}
                            className="flex-1 border-slate-700 bg-slate-900 hover:bg-slate-800 text-white py-6"
                            data-testid="google-login-btn"
                          >
                            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Google
                          </Button>
                        )}
                        
                        {DISCORD_LOGIN_ENABLED && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleDiscordAuth}
                            disabled={isGoogleLoading}
                            className="flex-1 border-slate-700 bg-slate-900 hover:bg-slate-800 text-white py-6"
                            data-testid="discord-login-btn"
                          >
                            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="#5865F2">
                              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                            </svg>
                            Discord
                          </Button>
                        )}
                        
                        {GITHUB_LOGIN_ENABLED && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleGitHubAuth}
                            disabled={isGoogleLoading}
                            className="flex-1 border-slate-700 bg-slate-900 hover:bg-slate-800 text-white py-6"
                            data-testid="github-login-btn"
                          >
                            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                            </svg>
                            GitHub
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </form>
              ) : (
                /* Register Form */
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300 text-sm">First Name</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                        <Input
                          type="text"
                          value={registerData.first_name}
                          onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                          className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-purple-500"
                          placeholder="John"
                          required
                          data-testid="register-firstname"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Last Name</Label>
                      <Input
                        type="text"
                        value={registerData.last_name}
                        onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                        className="mt-1 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-purple-500"
                        placeholder="Doe"
                        required
                        data-testid="register-lastname"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300 text-sm">Email Address</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                      <Input
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-purple-500"
                        placeholder="you@example.com"
                        required
                        data-testid="register-email"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300 text-sm">Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="pl-10 pr-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-purple-500"
                        placeholder="••••••••••••"
                        required
                        data-testid="register-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <PasswordRequirements password={registerData.password} show={registerData.password.length > 0} />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={registerData.rememberMe}
                      onCheckedChange={(checked) => setRegisterData({ ...registerData, rememberMe: checked })}
                      className="border-slate-600 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                    />
                    <span className="text-sm text-slate-400">
                      I agree to the{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300">Terms</a>
                      {' '}and{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300">Privacy Policy</a>
                    </span>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isLoading || (registerData.password && !validatePassword(registerData.password).isValid)}
                    className="w-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 py-6 text-base font-medium shadow-lg shadow-purple-500/25 transition-all hover:shadow-purple-500/40"
                    data-testid="register-submit-btn"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating Account...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Create Account
                        <Sparkles className="h-5 w-5" />
                      </span>
                    )}
                  </Button>
                  
                  {/* Google Login - temporarily hidden */}
                  {GOOGLE_LOGIN_ENABLED && (
                    <>
                      <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-slate-700" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-slate-950 px-2 text-slate-500">Or continue with</span>
                        </div>
                      </div>
                      
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleGoogleAuth}
                        disabled={isGoogleLoading}
                        className="w-full border-slate-700 bg-slate-900 hover:bg-slate-800 text-white py-6"
                        data-testid="google-register-btn"
                      >
                        <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </Button>
                    </>
                  )}
                </form>
              )}
              
              {/* Referral badge */}
              {referralCode && (
                <div className="mt-6 p-3 rounded-lg bg-gradient-to-r from-purple-900/50 to-fuchsia-900/50 border border-purple-500/30">
                  <p className="text-sm text-purple-200 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-yellow-400" />
                    Referral bonus: Get 5 free credits when you sign up!
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Footer links */}
          <div className="mt-8 text-center text-sm text-slate-500">
            <Link to="/" className="hover:text-purple-400 transition-colors">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
